/**
 * flac-chunked-decode.js — streaming FLAC decoder that never holds the whole
 * file decoded at once. Companion to mp3-chunked-decode.js, same shape.
 *
 * Why this exists
 * ---------------
 * `decodeAudioData(file)` turns the entire compressed file into one PCM
 * AudioBuffer. A 10-minute 24-bit FLAC is ~170 MB compressed and ~230 MB
 * decoded, and the mastering chain then works at roughly four times that —
 * measured 2026-07-31: 906 MB of retained heap on a 10-minute track, growing
 * ~90 MB for every extra minute. On the 2 GB machines that actually report the
 * failures ("Array buffer allocation failed", "startRendering failed to create
 * AudioBuffer") that allocation is the wall.
 *
 * FLAC is a flat sequence of self-describing frames after its metadata blocks,
 * so the same trick that works for MP3 works here with one extra step: a frame
 * slice is NOT a valid stream on its own, it needs the `fLaC` magic and the
 * STREAMINFO block in front of it. Prepend those and each group of frames
 * decodes standalone. Verified on a real 471 s / 44.1 kHz / 24-bit file before
 * this module was written — the browser accepted the synthesised slice and gave
 * back real audio.
 *
 * A frame boundary is not guessed. The 14-bit sync pattern alone matches inside
 * compressed residuals often enough to matter, so every candidate is confirmed
 * by the CRC-8 that terminates the frame header.
 *
 *   ⚠️ decodeAudioData resamples to the CONTEXT's sample rate. Decode into an
 *   OfflineAudioContext built at the file's own rate — `parseHeader()` gives it
 *   to you — or a 44.1 kHz master silently becomes 48 kHz, once per chunk.
 *
 * Usage
 *   const hdr = FlacChunkedDecode.parseHeader(new Uint8Array(await file.slice(0, 65536).arrayBuffer()));
 *   const ctx = new OfflineAudioContext(hdr.channels, 1, hdr.sampleRate);
 *   await FlacChunkedDecode.decodeChunks(file, ctx, {
 *       chunkSec: 30,
 *       onChunk: (audioBuffer, offsetSec, i, total) => { ... },
 *       onProgress: (ratio) => { ... },
 *   });
 */
(function () {
    'use strict';

    function FlacChunkedDecodeError(msg) {
        var e = new Error(msg);
        e.name = 'FlacChunkedDecodeError';
        return e;
    }

    function looksLikeFlac(file) {
        if (!file) return false;
        var name = (file.name || '').toLowerCase();
        var type = (file.type || '').toLowerCase();
        if (name.slice(-5) === '.flac') return true;
        return type === 'audio/flac' || type === 'audio/x-flac';
    }

    /* Metadata: "fLaC" then a chain of blocks, each with a 4-byte header whose
       top bit marks the last one. STREAMINFO is type 0 and mandatory. */
    function parseHeader(bytes) {
        if (!bytes || bytes.length < 42) throw FlacChunkedDecodeError('too short for a FLAC header');
        if (bytes[0] !== 0x66 || bytes[1] !== 0x4c || bytes[2] !== 0x61 || bytes[3] !== 0x43) {
            throw FlacChunkedDecodeError('missing fLaC magic');
        }
        var off = 4, streamInfo = null, last = false, guard = 0;
        while (!last) {
            if (off + 4 > bytes.length || ++guard > 256) throw FlacChunkedDecodeError('metadata blocks truncated');
            last = (bytes[off] & 0x80) !== 0;
            var type = bytes[off] & 0x7f;
            var len = (bytes[off + 1] << 16) | (bytes[off + 2] << 8) | bytes[off + 3];
            if (type === 0) {
                if (len < 34 || off + 4 + len > bytes.length) throw FlacChunkedDecodeError('STREAMINFO truncated');
                streamInfo = bytes.subarray(off, off + 4 + len);
            }
            off += 4 + len;
        }
        if (!streamInfo) throw FlacChunkedDecodeError('no STREAMINFO');
        var si = streamInfo.subarray(4);
        var sampleRate = (si[10] << 12) | (si[11] << 4) | (si[12] >> 4);
        var channels = ((si[12] >> 1) & 0x07) + 1;
        var bitsPerSample = (((si[12] & 1) << 4) | (si[13] >> 4)) + 1;
        // 36-bit total sample count; the top 4 bits ride in si[13]
        var totalSamples = (si[13] & 0x0f) * 4294967296 +
            (si[14] * 16777216) + (si[15] << 16) + (si[16] << 8) + si[17];
        if (!sampleRate) throw FlacChunkedDecodeError('STREAMINFO reports no sample rate');
        return {
            sampleRate: sampleRate, channels: channels, bitsPerSample: bitsPerSample,
            totalSamples: totalSamples, durationSec: totalSamples ? totalSamples / sampleRate : 0,
            minBlock: (si[0] << 8) | si[1], maxBlock: (si[2] << 8) | si[3],
            firstFrame: off, streamInfo: streamInfo,
        };
    }

    function crc8(bytes, from, to) {
        var crc = 0;
        for (var i = from; i < to; i++) {
            crc ^= bytes[i];
            for (var b = 0; b < 8; b++) crc = (crc & 0x80) ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
        }
        return crc;
    }

    // Where the coded frame/sample number ends: it is stored UTF-8 style, so the
    // leading byte's high bits give the length.
    function codedNumberLength(first) {
        if (first < 0x80) return 1;
        if (first >= 0xfe) return 7;
        if (first >= 0xfc) return 6;
        if (first >= 0xf8) return 5;
        if (first >= 0xf0) return 4;
        if (first >= 0xe0) return 3;
        if (first >= 0xc0) return 2;
        return 0;                       // 10xxxxxx is a continuation byte — invalid here
    }

    // Total header length INCLUDING the trailing CRC-8 byte, or 0 if malformed.
    function headerLength(bytes, p) {
        if (p + 5 > bytes.length) return 0;
        var bsCode = bytes[p + 2] >> 4, srCode = bytes[p + 2] & 0x0f;
        if (bsCode === 0 || srCode === 15) return 0;                 // reserved / invalid
        var cn = codedNumberLength(bytes[p + 4]);
        if (!cn) return 0;
        var q = p + 4 + cn;
        if (bsCode === 6) q += 1; else if (bsCode === 7) q += 2;
        if (srCode === 12) q += 1; else if (srCode === 13 || srCode === 14) q += 2;
        return (q + 1) - p;
    }

    function isFrameStart(bytes, p) {
        if (p + 6 > bytes.length) return false;
        if (bytes[p] !== 0xff || (bytes[p + 1] & 0xfc) !== 0xf8) return false;
        var hl = headerLength(bytes, p);
        if (hl < 5 || p + hl > bytes.length) return false;
        return crc8(bytes, p, p + hl - 1) === bytes[p + hl - 1];
    }

    // Samples in this frame, from its block-size code.
    function blockSizeAt(bytes, p) {
        var bsCode = bytes[p + 2] >> 4;
        if (bsCode === 1) return 192;
        if (bsCode >= 2 && bsCode <= 5) return 576 << (bsCode - 2);
        if (bsCode >= 8 && bsCode <= 15) return 256 << (bsCode - 8);
        var at = p + 4 + codedNumberLength(bytes[p + 4]);
        if (bsCode === 6) return bytes[at] + 1;
        if (bsCode === 7) return ((bytes[at] << 8) | bytes[at + 1]) + 1;
        return 0;
    }

    function nextFrame(bytes, from) {
        for (var p = from; p + 6 <= bytes.length; p++) if (isFrameStart(bytes, p)) return p;
        return -1;
    }

    /* Group frames into ≈chunkSec windows. Returns null when the stream cannot be
       walked, so the caller falls back to a whole-file decode instead of guessing. */
    function planChunks(bytes, chunkSec) {
        var hdr;
        try { hdr = parseHeader(bytes); } catch (e) { return null; }
        var target = Math.max(1, chunkSec || 30) * hdr.sampleRate;
        var p = nextFrame(bytes, hdr.firstFrame);
        if (p < 0) return null;

        var plan = [], start = p, samples = 0, offsetSamples = 0, frames = 0;
        while (p >= 0 && p < bytes.length) {
            var bs = blockSizeAt(bytes, p);
            if (!bs) return null;
            var nxt = nextFrame(bytes, p + 5);
            samples += bs; frames++;
            if (samples >= target || nxt < 0) {
                plan.push({
                    start: start, end: nxt < 0 ? bytes.length : nxt,
                    offsetSec: offsetSamples / hdr.sampleRate,
                    durSec: samples / hdr.sampleRate, frames: frames,
                });
                offsetSamples += samples;
                samples = 0; frames = 0; start = nxt;
            }
            p = nxt;
        }
        return plan.length ? { header: hdr, chunks: plan } : null;
    }

    // "fLaC" + STREAMINFO, with the last-block flag set and the total sample
    // count zeroed — a slice really is a stream of unknown length, so declaring
    // the whole file's length in every chunk is a lie about the data.
    // (Chrome decodes correctly either way — measured, the mutation survives an
    // end-to-end test — so this is correctness by the spec, not a workaround for
    // an observed failure. It is asserted at unit level instead.)
    function prefixFor(hdr) {
        var si = new Uint8Array(hdr.streamInfo);
        si[0] |= 0x80;
        si[4 + 13] &= 0xf0;
        si[4 + 14] = 0; si[4 + 15] = 0; si[4 + 16] = 0; si[4 + 17] = 0;
        var out = new Uint8Array(4 + si.length);
        out[0] = 0x66; out[1] = 0x4c; out[2] = 0x61; out[3] = 0x43;
        out.set(si, 4);
        return out;
    }

    async function decodeChunks(file, ctx, opts) {
        opts = opts || {};
        if (!file) throw FlacChunkedDecodeError('no file');
        if (!ctx || !ctx.decodeAudioData) throw FlacChunkedDecodeError('no AudioContext');
        var signal = opts.signal;

        var ab = await file.arrayBuffer();
        var bytes = new Uint8Array(ab);
        var planned = planChunks(bytes, opts.chunkSec || 30);
        if (!planned) throw FlacChunkedDecodeError('FLAC frame walk failed — not a clean FLAC');

        var hdr = planned.header, list = planned.chunks, total = list.length;
        var prefix = prefixFor(hdr), totalDur = 0;
        for (var i = 0; i < total; i++) {
            if (signal && signal.aborted) throw FlacChunkedDecodeError('aborted');
            var seg = list[i];
            var body = bytes.subarray(seg.start, seg.end);
            var sub = new Uint8Array(prefix.length + body.length);
            sub.set(prefix, 0); sub.set(body, prefix.length);
            // decodeAudioData detaches what it is given, so each chunk hands over
            // its own buffer and the source bytes stay usable for the next one.
            var audioBuffer = await ctx.decodeAudioData(sub.buffer);
            totalDur = seg.offsetSec + (audioBuffer.duration || seg.durSec);
            if (opts.onChunk) await opts.onChunk(audioBuffer, seg.offsetSec, i, total);
            if (opts.onProgress) { try { opts.onProgress((i + 1) / total); } catch (_) {} }
        }
        return { chunks: total, totalDurationSec: totalDur, header: hdr };
    }

    window.FlacChunkedDecode = {
        looksLikeFlac: looksLikeFlac,
        parseHeader: parseHeader,
        planChunks: planChunks,
        decodeChunks: decodeChunks,
        // exposed for unit tests
        _isFrameStart: isFrameStart,
        _prefixFor: prefixFor,
        _codedNumberLength: codedNumberLength,
        _headerLength: headerLength,
        _blockSizeAt: blockSizeAt,
        _crc8: crc8,
        version: '1.0.0',
    };

    if (typeof window.__drainSharedModule === 'function') window.__drainSharedModule('FlacChunkedDecode');

})();
